import { faBookMedical, faChevronDown, faChevronUp, faReceipt, faRefresh } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { Badge, Button, Card, Col, OverlayTrigger, Row, Table, Tooltip } from "react-bootstrap";
import { EXPLAIN_DDX, SCAN_MULTI } from "../../../amplify/api-gateway/write";
import CopyToClipboardButton from "../../assistant/CopyToClipboardButton";
import { GenerateModuleHeroV2 } from "../../common/GenerateModuleHeroV2";
import { FakeLoadingBar } from "../../dashboard/llm/common/FakeLoadingBar";
import { LLMDashboardFeedbackBar } from "../../dashboard/llm/common/LLMDashboardFeedbackBar";
import { LLMDashboardUpdated } from "../../dashboard/llm/common/LLMDashboardUpdated";
import { isSectionUnread, processTextForCopy } from "../../dashboard/llm/utils";
import { dashboardLLMCache } from "../../dashboard/llm/cache";
import { LLMDashboardMarkdownRenderer } from "../../dashboard/llm/common/LLMDashboardMarkdownRenderer";
import { getLocalDateStrFromDate, getLocalTimeStrFromDate } from "../../../utils/time";


export const SectionTrackboardDDX = ({
    user,
    tenant,
    className='',
    isLoadingDdx,
    ddx,
    patientVisit,
    feedbacks,
    setFeedbacks,
    statuses,
    setRegenerateTarget,
    setShowContextMenu,
    handleShowScanMultiModal,
    unreadFileVersionMap = undefined,
    latestFileVersions = undefined,
    sectionToSetter = undefined,
    handleFileVersionChange = undefined,
}: any) => {
    const [isExpanded, setIsExpanded] = useState(true);
    const [scanMulti, setScanMulti] = useState();

    useEffect(() => {
        // It's important to load the version of the file that the explain data
        // was generated from to ensure the citations line up
        if (patientVisit && latestFileVersions && latestFileVersions[EXPLAIN_DDX]) {
            const version = latestFileVersions[EXPLAIN_DDX];

            dashboardLLMCache.get(SCAN_MULTI, patientVisit, tenant, version.source_version_id)
                .then((data: any) => {
                    setScanMulti(data);
                });
        }
    }, [latestFileVersions]);

    if (!isLoadingDdx && (!ddx || ddx["error"])) {
        return (
            <GenerateModuleHeroV2 type={EXPLAIN_DDX} setRegenerateTarget={setRegenerateTarget} setShowContextMenu={setShowContextMenu} />
        )
    }

    const getTextToCopy = (includeEvidence?: boolean) => {
        let anchorDate = new Date();
        if (latestFileVersions && latestFileVersions[EXPLAIN_DDX]) {
            anchorDate = new Date(latestFileVersions[EXPLAIN_DDX].version_date);
        }

        // For basic "Copy DDX": remove parenthetical timestamps for cleaner clinical copy-paste
        // For "Copy DDX with Evidence": keep all timestamps intact for full detail
        const formatText = (str: string) => includeEvidence ? str : processTextForCopy(str, anchorDate);

        let text = `DDX as of ${getLocalDateStrFromDate(anchorDate)} ${getLocalTimeStrFromDate(anchorDate)}. ` + formatText(ddx.ddx_summary) + "\n";
        if (ddx && ddx.prioritized_diagnoses && includeEvidence) {
            ddx.prioritized_diagnoses.forEach((data: any, idx: number) => {
                text += "\n\n" + data.diagnosis + ": " + formatText(data.justification);
                if (data.evidence) {
                    data.evidence.forEach((evi: any) => {
                        text += "\nEvidence:\n" + "- Source: " + formatText(evi.source) + "\n- Details: " + formatText(evi.details);
                    });
                }
            });
        }
        return text;
    };

    return (
        <Card className={`fade-in ${className}`}>
            <FakeLoadingBar isActive={isLoadingDdx} maxDuration={30000} />
            <Card.Body>
                <Row>
                    <Col md={4}>
                        <div className="d-flex align-items-center">
                            <span className={`dashboard-actions-circle-small dashboard-actions-circle-blue me-2`}><FontAwesomeIcon icon={faBookMedical} fixedWidth /></span><strong>Differential Diagnosis</strong> {isSectionUnread(EXPLAIN_DDX, unreadFileVersionMap) && <Badge bg="info" className="ms-2">NEW</Badge>}
                        </div>
                    </Col>
                    <Col md={8} className="text-right">
                        <LLMDashboardUpdated patient={patientVisit} statuses={statuses} handleShowScanMultiModal={handleShowScanMultiModal} handleFileVersionChange={handleFileVersionChange} latestFileVersions={latestFileVersions} explainKey={EXPLAIN_DDX} sectionToSetter={sectionToSetter} />
            
                        <CopyToClipboardButton user={user} module={"trackboard_ddx"} onClick={() => {navigator.clipboard.writeText(getTextToCopy())}} customStyling="dashboard-round-btn" overlayTriggerMessage="Copy DDX" />
                        <CopyToClipboardButton user={user} module={"trackboard_ddx_with_evidence"} onClick={() => {navigator.clipboard.writeText(getTextToCopy(true))}} customStyling="dashboard-round-btn" overlayTriggerMessage="Copy DDX with Evidence" defaultIcon={faReceipt} />
                        <OverlayTrigger
                            placement="bottom"
                            overlay={
                                <Tooltip>
                                    Refresh Section
                                </Tooltip>
                            }
                        >
                            <Button
                                variant="primary"
                                className="dashboard-round-btn"
                                onClick={(e: any) => {e.stopPropagation(); setRegenerateTarget(EXPLAIN_DDX); setShowContextMenu(true)}}
                            >
                                <FontAwesomeIcon icon={faRefresh} />
                            </Button>
                        </OverlayTrigger>
                        <LLMDashboardFeedbackBar patientVisit={patientVisit} feedbackTarget={"ddx"} feedbacks={feedbacks} setFeedbacks={setFeedbacks} />
                        <Button
                            variant="primary"
                            className="dashboard-round-btn"
                            onClick={(e: any) => {e.stopPropagation(); setIsExpanded(!isExpanded)}}
                        >
                            <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
                        </Button>
                    </Col>
                </Row>
                {isExpanded && (
                    <>
                        <Row className="mt-2">
                            <Col md={12} className="description-text">
                                {
                                    ddx && ddx["prioritized_diagnoses"] ? (
                                        <Table className="text-small rhythm-table" hover>
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-4 py-2 text-left font-medium text-gray-600">Rank</th>
                                                    <th className="px-4 py-2 text-left font-medium text-gray-600">Diagnosis</th>
                                                    <th className="px-4 py-2 text-left font-medium text-gray-600">Justification</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {ddx["prioritized_diagnoses"].map((diagnosis: any, index: number) => {
                                                    if (!diagnosis) {
                                                        return <></>;
                                                    }
                                                    return (
                                                        <tr
                                                            key={diagnosis.diagnosis}
                                                            className={`bg-gray-50 hover:bg-gray-100`}
                                                        >
                                                            <td className="px-4 py-2 whitespace-nowrap">{index + 1}</td>
                                                            <td className="px-4 py-2 font-medium whitespace-nowrap">
                                                                <span className="text-gray-900">{diagnosis.diagnosis}</span>
                                                            </td>
                                                            <td className="px-4 py-2 whitespace-nowrap">
                                                                <LLMDashboardMarkdownRenderer content={diagnosis.justification} scanMulti={scanMulti} />
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </Table>
                                    ) : (
                                        <>No Data</>
                                    )
                                }
                            </Col>
                        </Row>
                        
                    </>
                )}
            </Card.Body>
        </Card>
    );
};
